// services.ts

// 서비스 클래스 임포트
import { AuthService } from 'services/AuthService';
import { CategoryService } from 'services/CategoryService';
import { CrawlerService } from 'services/CrawlerService';
import { HttpClient } from 'services/http-client';
import { MyAccountService } from 'services/MyAccountService';
import { OrderCleansingService } from 'services/OrderCleansingService';
import { UserService } from 'services/UserService';
import { SellerService } from 'services/SellerService';

// ※ locationService와 같이 사용하지 않는 서비스는 필요에 따라 제거하거나, 올바른 타입으로 교체하세요.
// import { LocationService } from 'services/LocationService';

/**
 * 서비스 인스턴스들의 타입 정의
 * 외부에 노출할 때는 모든 서비스가 초기화된 상태여야 하므로 필수 프로퍼티로 정의합니다.
 */
export type ServiceInstances = {
  // locationService: LocationService; // 사용 시 주석 해제 및 올바른 타입 지정
  authService: AuthService;
  categoryService: CategoryService;
  crawlerService: CrawlerService;
  orderCleansingService: OrderCleansingService;
  userService: UserService;
  myAccountService: MyAccountService;
  sellerService: SellerService;
  reset: () => void;
  // 다른 서비스 타입을 추가하세요.
};

/**
 * ServiceHelper 클래스는 HttpClient를 기반으로 모든 서비스를 초기화하고,
 * Proxy를 통해 안전하게 접근할 수 있도록 합니다.
 */
class ServiceHelper {
  /**
   * 내부 서비스 저장소. 초기화 전에는 Partial<>로 관리합니다.
   */
  private services: Partial<ServiceInstances> = {};
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this.loadServices();
  }

  /**
   * 각 서비스 클래스를 명시적으로 인스턴스화하여 저장합니다.
   * 서비스가 추가되면 이 메서드와 ServiceInstances 타입에 함께 등록하세요.
   */
  private loadServices(): void {
    this.services = {
      authService: new AuthService(this.httpClient),
      categoryService: new CategoryService(this.httpClient),
      crawlerService: new CrawlerService(this.httpClient),
      orderCleansingService: new OrderCleansingService(this.httpClient),
      userService: new UserService(this.httpClient),
      myAccountService: new MyAccountService(this.httpClient),
      sellerService: new SellerService(this.httpClient),
      // locationService: new LocationService(this.httpClient), // 필요 시 추가
    };
  }

  /**
   * 서비스들을 재초기화하는 인스턴스 메서드
   * (예: 인증 토큰 갱신 등으로 HttpClient의 내부 상태가 변경된 경우 호출)
   */
  public reset(): void {
    console.log('Resetting services...');
    this.loadServices();
    console.log('새로운 서비스 인스턴스:', this.services);
    // this.loadServices();
  }

  /**
   * Proxy를 통해 서비스에 접근합니다.
   * 매번 내부 this.services를 참조하므로, reset() 후에도 최신 인스턴스가 반영됩니다.
   * 또한, 'reset' 프로퍼티에 접근하면 this.reset() 함수를 반환합니다.
   */
  public getServiceProxy(): ServiceInstances {
    return new Proxy(
      {},
      {
        get: (target, prop, receiver) => {
          // 'reset' 프로퍼티는 인스턴스의 reset 메서드를 반환
          if (prop === 'reset') {
            return this.reset.bind(this);
          }
          // 나머지 프로퍼티는 내부 this.services에서 읽어옵니다.
          if (prop in this.services) {
            const service = this.services[prop as keyof ServiceInstances];
            if (service !== undefined) {
              return service;
            }
          }
          throw new Error(`Service '${String(prop)}' has not been initialized.`);
        },
      },
    ) as ServiceInstances;
  }
}

// HttpClient 인스턴스 생성 및 ServiceHelper 초기화 (싱글턴)
const httpClient = new HttpClient();
const serviceHelper = new ServiceHelper(httpClient);

/**
 * 싱글턴 패턴: 애플리케이션 전역에서 동일한 서비스 인스턴스에 접근할 수 있도록 export
 * (페이지 내 여러 호출은 물론, 다른 페이지에서 새로 호출해도 Proxy가 항상 최신 this.services를 참조합니다.)
 */
export default serviceHelper.getServiceProxy();
